import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AudienceSpec,
  Form,
  FormField,
  FormFieldType,
  FormHandling,
  Submission,
  SubmissionList,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardFoot,
  CardHead,
  CardPad,
  Checkbox,
  Drawer,
  Empty,
  Field,
  IconBtn,
  Input,
  Note,
  PageHead,
  PersonCell,
  Row,
  Select,
  Stack,
  Table,
  Textarea,
  useAdminToast,
  type BadgeTone,
} from '../admin-kit';
import { AudienceField } from '../beheer-communicatie/audience-field';
import { LoadError, Loading, usePageError } from '../beheer-communicatie/page-state';
import { saveBlob } from '../../shared/download';
import { parseOptionLines } from '../../shared/option-lines';
import { useSession } from '../../shared/session';
import { formatDateTime } from '../../shared/format';
import { labelOf } from './field-input';
import { SubmissionRow } from './submission-row';
import { displayStatus, type DisplayStatus } from './submission-display';
import { HandlingFields, handlingText, useGroups } from './forms-list-page';
import { audienceIncomplete } from '../../shared/audience-preview';

/** De veldtypes in de volgorde waarin een beheerder ze kiest: tekst eerst, foto achteraan. */
const FIELD_TYPES: FormFieldType[] = [
  'text',
  'longtext',
  'number',
  'date',
  'daterange',
  'choice',
  'multichoice',
  'photo',
];

const STATUS_TONE: Record<DisplayStatus, BadgeTone> = {
  submitted: 'attention',
  // Alleen verzamelen: er wacht niets (testrapport 28 okt., #8).
  received: 'neutral',
  approved: 'ok',
  rejected: 'danger',
  withdrawn: 'neutral',
};

/**
 * De formulierbouwer op zijn eigen pagina (Stap 37, nieuw ontwerp in BHR-5, frame C6).
 *
 * Links de **definitie** (de velden, die naar het concept gaan), rechts de instellingen en de
 * laatste **aanvragen**. Twee soorten werk, en een beheerder die een veld bijzet, is niet degene
 * die op dat moment een verlofaanvraag beoordeelt.
 *
 * Bovenaan staat altijd welke versie geldt. Zonder die regel is de belangrijkste eigenschap van
 * deze module onzichtbaar: wat je hier wijzigt, geldt pas na publiceren, en de aanvragen van
 * vorige maand blijven bij hun eigen versie.
 */
export function FormBuilderPage({ formId }: { formId: string }) {
  const { t } = useTranslation();
  const { api } = useSession();

  const form = useQuery({
    queryKey: ['forms', formId],
    queryFn: async (): Promise<Form> => {
      const { data, error } = await api.GET('/api/v1/forms/{formId}', {
        params: { path: { formId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as Form;
    },
  });

  const crumb = { label: t('formsAdmin.title'), to: '/admin/formulieren' };

  if (form.isPending || form.isError) {
    return (
      <AdminPage>
        <PageHead crumb={crumb} title={t('formsAdmin.title')} />
        <Card>
          {form.isPending ? (
            <Loading />
          ) : (
            <LoadError onRetry={() => void form.refetch()} error={form.error} />
          )}
        </Card>
      </AdminPage>
    );
  }

  // Een nieuwe sleutel per formulier: de velden beginnen dan opnieuw van wat de server heeft.
  return <Builder key={form.data.id} form={form.data} />;
}

function Builder({ form }: { form: Form }) {
  const { t, i18n } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useAdminToast();
  const pageError = usePageError();

  const [fields, setFields] = useState<FormField[]>(
    form.draftVersion?.fields ?? form.publishedVersion?.fields ?? [],
  );
  const [editing, setEditing] = useState<number | null>(null);
  const [dragged, setDragged] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/api/v1/forms/{formId}', {
        params: { path: { formId: form.id } },
        body: { fields },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      pageError.clear();
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      toast.show(t('formsAdmin.draftSaved'));
    },
    onError: (error) => pageError.show(problemDetail(error) ?? t('errors.generic')),
  });

  const publish = useMutation({
    mutationFn: async () => {
      // Eerst de velden op het scherm als concept bewaren: de server publiceert het opgeslagen
      // concept, en niet-bewaarde wijzigingen zouden anders verloren gaan (of een 409 geven).
      const saved = await api.PATCH('/api/v1/forms/{formId}', {
        params: { path: { formId: form.id } },
        body: { fields },
      });
      if (saved.error) throw saved.error;
      const { error } = await api.POST('/api/v1/forms/{formId}/publish', {
        params: { path: { formId: form.id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      pageError.clear();
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      toast.show(t('formsAdmin.published'));
    },
    onError: (error) => pageError.show(problemDetail(error) ?? t('errors.generic')),
  });

  /**
   * De export ophalen met het token, en daarna opslaan.
   *
   * Geen `<a href download>`: dat is een browsernavigatie zonder `Authorization`-header en geeft
   * altijd een `401`. Zie `shared/download.ts` — dezelfde fout zat eerder in Stap 19 en 33.
   */
  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.GET('/api/v1/forms/{formId}/submissions.csv', {
        params: { path: { formId: form.id } },
        parseAs: 'blob',
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await saveBlob(data as unknown as Blob, `${form.title}.csv`);
    },
    onError: () => pageError.show(t('errors.generic')),
  });

  const update = (index: number, changes: Partial<FormField>) =>
    setFields((current) =>
      current.map((field, position) =>
        position === index ? ({ ...field, ...changes } as FormField) : field,
      ),
    );

  const addField = () => {
    setFields((current) => [
      ...current,
      {
        key: `veld_${current.length + 1}`,
        type: 'text',
        label: { nl: '' },
        required: false,
        options: [],
        min: null,
        max: null,
        prefill: 'none',
        prefillKey: null,
        locked: false,
      } as FormField,
    ]);
    // Het nieuwe veld staat meteen open: wie "Veld toevoegen" kiest, wil het een naam geven.
    setEditing(fields.length);
  };

  const remove = (index: number) => {
    setFields((current) => current.filter((_, position) => position !== index));
    setEditing((current) =>
      current === null || current === index ? null : current > index ? current - 1 : current,
    );
  };

  /** Eén veld naar een andere plaats; het veld dat openstaat, blijft open. */
  const move = (from: number, to: number) => {
    if (to < 0 || to >= fields.length || from === to) return;
    setFields((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved as FormField);
      return next;
    });
    setEditing((current) => {
      if (current === null) return null;
      if (current === from) return to;
      if (from < current && to >= current) return current - 1;
      if (from > current && to <= current) return current + 1;
      return current;
    });
  };

  const draftState = form.draftVersion
    ? t('beheer.communicatie.forms.draftState')
    : form.publishedVersion
      ? t('beheer.communicatie.forms.liveState', {
          number: form.publishedVersion.versionNumber,
        })
      : t('formsAdmin.notPublished');

  return (
    <AdminPage>
      <PageHead
        crumb={{ label: t('formsAdmin.title'), to: '/admin/formulieren' }}
        title={form.title}
        sub={`${handlingText(t, form)} · ${form.audienceDescription}`}
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="download" />}
              disabled={download.isPending}
              testId="form-export"
              onClick={() => download.mutate()}
            >
              {t('formsAdmin.export')}
            </Btn>
            <Btn
              variant="sec"
              disabled={save.isPending}
              testId="form-save-draft"
              onClick={() => save.mutate()}
            >
              {t('formsAdmin.saveDraft')}
            </Btn>
            <Btn
              icon={<AdminIcon name="send" />}
              disabled={fields.length === 0 || publish.isPending}
              testId="form-publish"
              onClick={() => publish.mutate()}
            >
              {t('formsAdmin.publish')}
            </Btn>
          </>
        }
      />

      {pageError.element}

      {/* Nieuwe aanvragen zouden blijven liggen zonder dat iemand ze ziet (hertest 29 okt.). */}
      {form.handlerMissing && !form.archived ? (
        <Note tone="danger" role="alert" lead={t('formsAdmin.handlerMissing')}>
          {t('formsAdmin.handlerMissingHint')}
        </Note>
      ) : null}

      <Card>
        <CardPad>
          <Note
            tone="info"
            lead={
              form.publishedVersion
                ? t('formsAdmin.liveSince', {
                    number: form.publishedVersion.versionNumber,
                    count: form.publishedVersion.submissionCount,
                  })
                : t('formsAdmin.notPublishedHint')
            }
          >
            {t('beheer.communicatie.forms.versionNotice')}
          </Note>
        </CardPad>
      </Card>

      <div className="a-grid side">
        <Card testId="form-editor">
          <CardHead
            title={t('beheer.communicatie.forms.fields')}
            count={fields.length}
            actions={<span className="a-meta">{draftState}</span>}
          />
          <ul className="c-list">
            {fields.map((field, index) => {
              const label = labelOf(field.label, i18n.language, '');
              const name = label || t('beheer.communicatie.forms.noLabel');
              const open = editing === index;
              return (
                <li
                  key={index}
                  className={open ? 'a-item c-field-row c-open' : 'a-item c-field-row'}
                  data-testid="field-row"
                  /*
                    Slepen met de muis, en in het open veld knoppen voor de rest: `draggable` alleen
                    zou de volgorde onbereikbaar maken voor wie met een toetsenbord werkt (zoals bij
                    de snelkoppelingen, Extra 6 punt 10). Een open veld sleept niet: daar selecteer
                    je tekst.
                  */
                  draggable={!open}
                  onDragStart={() => setDragged(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (dragged !== null) move(dragged, index);
                    setDragged(null);
                  }}
                >
                  <AdminIcon name="grip" className="c-grip" />
                  {open ? (
                    <FieldEditor
                      field={field}
                      index={index}
                      count={fields.length}
                      onChange={(changes) => update(index, changes)}
                      onMove={(to) => move(index, to)}
                      onDone={() => setEditing(null)}
                    />
                  ) : (
                    <>
                      <div className="a-item-info">
                        <div className="a-item-top">
                          <span className="a-name">{name}</span>
                          <Badge tone="info">{t(`formsAdmin.type.${field.type}`)}</Badge>
                          {field.required ? (
                            <Badge tone="navy">{t('formsAdmin.required')}</Badge>
                          ) : null}
                        </div>
                        <div className="a-meta a-mono">{field.key}</div>
                      </div>
                      <IconBtn
                        label={t('beheer.communicatie.forms.editField', { name })}
                        icon={<AdminIcon name="pencil" />}
                        testId={`field-edit-${index}`}
                        onClick={() => setEditing(index)}
                      />
                    </>
                  )}
                  <IconBtn
                    label={t('beheer.communicatie.forms.removeField', { name })}
                    icon={<AdminIcon name="trash" />}
                    testId={`field-remove-${index}`}
                    onClick={() => remove(index)}
                  />
                </li>
              );
            })}
            {fields.length === 0 ? (
              <li className="a-item a-muted">{t('beheer.communicatie.forms.noFields')}</li>
            ) : null}
            <li className="a-item">
              <Btn
                variant="sec"
                size="sm"
                icon={<AdminIcon name="plus" />}
                testId="field-add"
                onClick={addField}
              >
                {t('formsAdmin.addField')}
              </Btn>
            </li>
          </ul>
        </Card>

        <Stack>
          <SettingsCard
            form={form}
            onSaved={() => {
              pageError.clear();
              toast.show(t('beheer.communicatie.forms.settingsSaved'));
            }}
            onFailed={pageError.show}
          />
          <SubmissionsCard form={form} />
        </Stack>
      </div>

      {toast.element}
    </AdminPage>
  );
}

/** Het veld dat openstaat: label en sleutel, soort, en wat bij die soort hoort. */
function FieldEditor({
  field,
  index,
  count,
  onChange,
  onMove,
  onDone,
}: {
  field: FormField;
  index: number;
  count: number;
  onChange: (changes: Partial<FormField>) => void;
  onMove: (to: number) => void;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  // De ruwe tekst van het optieveld blijft hier staan; zie `shared/option-lines.ts`.
  const [optionsText, setOptionsText] = useState(() =>
    field.options.map((option) => labelOf(option.label, i18n.language, option.value)).join('\n'),
  );

  return (
    <div className="a-item-info">
      <Row>
        <Field label={t('formsAdmin.fieldLabel')}>
          {(props) => (
            <Input
              {...props}
              value={labelOf(field.label, i18n.language, '')}
              onChange={(event) => onChange({ label: { ...field.label, nl: event.target.value } })}
              data-testid={`field-label-${index}`}
            />
          )}
        </Field>
        <Field label={t('formsAdmin.fieldKey')} hint={t('beheer.communicatie.forms.keyHint')}>
          {(props) => (
            <Input
              {...props}
              className="a-mono"
              value={field.key}
              onChange={(event) => onChange({ key: event.target.value })}
              data-testid={`field-key-${index}`}
            />
          )}
        </Field>
      </Row>

      <Field label={t('formsAdmin.fieldType')}>
        {(props) => (
          <Select
            {...props}
            value={field.type}
            onChange={(event) => {
              setOptionsText('');
              onChange({
                type: event.target.value as FormFieldType,
                // Een ander type betekent andere mogelijkheden: opties en grenzen die er niet bij
                // horen, weigert de server, dus ze gaan hier weg.
                options: [],
                min: null,
                max: null,
                prefill: 'none',
                prefillKey: null,
                locked: false,
              });
            }}
            data-testid={`field-type-${index}`}
          >
            {FIELD_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`formsAdmin.type.${type}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {field.type === 'choice' || field.type === 'multichoice' ? (
        <Field
          label={t('beheer.communicatie.forms.options')}
          hint={t('beheer.communicatie.forms.optionsHint')}
        >
          {(props) => (
            <Textarea
              {...props}
              // Eén optie per regel: een beheerder die acht keuzes invoert, wil niet acht keer op
              // "toevoegen" drukken. De waarde is het label in kleine letters met streepjes,
              // zodat de export leesbare sleutels krijgt.
              value={optionsText}
              onChange={(event) => {
                setOptionsText(event.target.value);
                onChange({ options: parseOptionLines(event.target.value) });
              }}
              data-testid={`field-options-${index}`}
            />
          )}
        </Field>
      ) : null}

      {field.type === 'number' ? (
        <Row>
          <Field label={t('formsAdmin.min')} optional>
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={field.min === null ? '' : String(field.min)}
                onChange={(event) =>
                  onChange({ min: event.target.value === '' ? null : Number(event.target.value) })
                }
              />
            )}
          </Field>
          <Field label={t('formsAdmin.max')} optional>
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={field.max === null ? '' : String(field.max)}
                onChange={(event) =>
                  onChange({ max: event.target.value === '' ? null : Number(event.target.value) })
                }
              />
            )}
          </Field>
        </Row>
      ) : null}

      {field.type === 'text' ? (
        <>
          <Field label={t('formsAdmin.prefill')} hint={t('formsAdmin.prefillHint')}>
            {(props) => (
              <Select
                {...props}
                value={field.prefill}
                onChange={(event) =>
                  onChange({
                    prefill: event.target.value as FormField['prefill'],
                    prefillKey: null,
                    locked: event.target.value === 'none' ? false : field.locked,
                  })
                }
                data-testid={`field-prefill-${index}`}
              >
                <option value="none">{t('formsAdmin.prefillNone')}</option>
                <option value="employee_number">{t('formsAdmin.prefillNumber')}</option>
                <option value="full_name">{t('formsAdmin.prefillName')}</option>
              </Select>
            )}
          </Field>
          {field.prefill !== 'none' ? (
            <Checkbox
              checked={field.locked}
              onChange={(locked) => onChange({ locked })}
              label={t('formsAdmin.locked')}
              testId={`field-locked-${index}`}
            />
          ) : null}
        </>
      ) : null}

      <Checkbox
        checked={field.required}
        onChange={(required) => onChange({ required })}
        label={t('formsAdmin.required')}
        testId={`field-required-${index}`}
      />

      <div className="a-actions">
        <Btn variant="sec" size="sm" onClick={onDone} testId={`field-done-${index}`}>
          {t('beheer.communicatie.done')}
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          icon={<AdminIcon name="arrowUp" />}
          disabled={index === 0}
          onClick={() => onMove(index - 1)}
        >
          {t('beheer.communicatie.moveUp')}
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          icon={<AdminIcon name="arrowDown" />}
          disabled={index === count - 1}
          onClick={() => onMove(index + 1)}
        >
          {t('beheer.communicatie.moveDown')}
        </Btn>
      </div>
    </div>
  );
}

/**
 * Doelgroep en behandeling (C6, rechts).
 *
 * Sinds FBK-7 geeft de API de doelgroep zelf terug, en is ze hier te wijzigen met de gewone kiezer.
 * Doelgroep en behandeling gelden meteen (ze zijn geen deel van een versie) en gaan daarom met een
 * eigen knop en niet mee met "Concept opslaan". Een nieuwe doelgroep geldt voor nieuwe aanvragen;
 * wat al ingediend is, blijft staan.
 */
function SettingsCard({
  form,
  onSaved,
  onFailed,
}: {
  form: Form;
  onSaved: () => void;
  onFailed: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const groups = useGroups();
  const [handling, setHandling] = useState<FormHandling>(form.handling);
  const [groupIds, setGroupIds] = useState<string[]>(form.handlerGroupIds);
  const [audience, setAudience] = useState<AudienceSpec | null>(form.audience);

  const audienceChanged =
    audience !== null && JSON.stringify(audience) !== JSON.stringify(form.audience);
  const changed =
    audienceChanged ||
    handling !== form.handling ||
    (handling === 'group' && groupIds.join() !== form.handlerGroupIds.join());
  // Ook geen lege doelgroep: "Kiezen" zonder keuze weigert de server (hertest 30 okt.).
  const complete =
    !(handling === 'group' && groupIds.length === 0) && !audienceIncomplete(audience);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/api/v1/forms/{formId}', {
        params: { path: { formId: form.id } },
        body: {
          handling,
          handlerGroupIds: handling === 'group' ? groupIds : [],
          ...(audienceChanged && audience ? { audience } : {}),
        } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      onSaved();
    },
    onError: (error) => onFailed(problemDetail(error) ?? t('errors.generic')),
  });

  return (
    <Card testId="form-settings">
      <CardHead title={t('beheer.communicatie.forms.settings')} />
      <CardPad>
        <div className="a-field">
          <div className="a-label">{t('beheer.communicatie.colAudience')}</div>
          {audience ? (
            <AudienceField value={audience} onChange={setAudience} testId="form-audience" />
          ) : (
            <Note tone="info" icon={<AdminIcon name="groups" size={18} />}>
              {form.audienceDescription}
            </Note>
          )}
        </div>
        <HandlingFields
          name={`form-handling-${form.id}`}
          handling={handling}
          onHandling={setHandling}
          groupIds={groupIds}
          onGroup={setGroupIds}
          groups={groups.data ?? []}
          groupTestId="form-settings-group"
        />
        {changed ? (
          <div>
            <Btn
              size="sm"
              disabled={!complete || save.isPending}
              testId="form-settings-save"
              onClick={() => save.mutate()}
            >
              {t('beheer.communicatie.forms.saveSettings')}
            </Btn>
          </div>
        ) : null}
      </CardPad>
    </Card>
  );
}

/**
 * De laatste drie aanvragen (C6, rechts), en via "Alle … aanvragen" de volledige lijst in een
 * paneel, met goedkeuren en afwijzen voor wie mag beslissen - zoals de bouwer dat al kon.
 */
function SubmissionsCard({ form }: { form: Form }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [allOpen, setAllOpen] = useState(false);

  const submissions = useQuery({
    queryKey: ['forms', 'submissions', form.id],
    queryFn: async (): Promise<Submission[]> => {
      const { data, error } = await api.GET('/api/v1/forms/submissions', {
        params: { query: { formId: form.id, all: 'true' } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as SubmissionList).items;
    },
  });

  const items = submissions.data ?? [];

  return (
    <Card testId="form-submissions">
      <CardHead
        title={t('formsAdmin.submissions')}
        count={submissions.isSuccess ? items.length : undefined}
      />
      {submissions.isPending ? (
        <Loading />
      ) : submissions.isError ? (
        <LoadError onRetry={() => void submissions.refetch()} error={submissions.error} />
      ) : items.length === 0 ? (
        <Empty
          icon={<AdminIcon name="inbox" size={24} />}
          title={t('formsAdmin.noSubmissionsTitle')}
          text={t('formsAdmin.noSubmissionsHint')}
        />
      ) : (
        <>
          <Table label={t('formsAdmin.submissions')}>
            <thead>
              <tr>
                <th>{t('beheer.communicatie.forms.applicant')}</th>
                <th>{t('beheer.communicatie.forms.status')}</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 3).map((submission) => (
                <tr key={submission.id}>
                  <td>
                    <PersonCell
                      name={submission.personName}
                      meta={formatDateTime(submission.submittedAt)}
                    />
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[displayStatus(submission)]}>
                      {t(`forms.status.${displayStatus(submission)}`)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <CardFoot>
            <button
              type="button"
              className="a-link"
              data-testid="form-submissions-all"
              onClick={() => setAllOpen(true)}
            >
              {t('beheer.communicatie.forms.allSubmissions', { count: items.length })}
            </button>
          </CardFoot>
        </>
      )}

      <Drawer
        open={allOpen}
        onClose={() => setAllOpen(false)}
        title={t('formsAdmin.submissions')}
        sub={form.title}
        testId="form-submissions-drawer"
        footer={<Btn onClick={() => setAllOpen(false)}>{t('common.close')}</Btn>}
      >
        <ul className="c-list a-stack">
          {items.map((submission) => (
            <li key={submission.id}>
              <SubmissionRow submission={submission} formId={form.id} />
            </li>
          ))}
        </ul>
      </Drawer>
    </Card>
  );
}
